/// <amd-module name="Repos/Views/Settings/Common/Services/SettingsViewService" />
import * as React from "react";
import { IReposMessagesService } from "Repos/Messages/ReposMessagesService";
import { IProjectData, ITfsPageService } from "Tfs/Platform/Page";
import * as StringUtil from "VSS/Core/Util/String";
import { IPageStateHistoryService } from "VSS/Features/History";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { routeUrl } from "VSS/Platform/Location";
import { MessageCardSeverity } from "VSSUI/MessageCard";

export interface ISettingsViewService extends IVssService {
    getUrl(repositoryId?: string, refName?: string, tabId?: string): string;
    getUrlParameters(): { [key: string]: string };
    showErrorMessage(error: any, format?: string): void;
}

export const enum SettingsTabIds {
    permissions = "permissions",
    policies = "policies",
    repositories = "repositories-tab",
    settings = "settings-tab",
    permissionsMid = "permissionsMid",
    policiesMid = "policiesMid",
    settingsMid = "settingsMid",
    checks = "checks",
}

export const enum SettingsQueryStringParams {
    repositoryId = "repo",
    refName = "refs",
    tabId = "_a"
}

export class SettingsViewService extends VssService implements ISettingsViewService {
    private messagesService: IReposMessagesService;
    private projectCache: IProjectData | undefined;

    public _serviceEnd = (pageContext: IVssPageContext): void => {
        super._serviceEnd(pageContext);
    };

    public _serviceStart = (pageContext: IVssPageContext): void => {
        super._serviceStart(pageContext);
        this.messagesService = this.pageContext.getService<IReposMessagesService>("IReposMessagesService");
    };

    public getUrl(repositoryId?: string, refName?: string, tabId?: string): string {
        let realRefName = refName;
        if (refName && tabId && refName.endsWith("/") && (tabId === SettingsTabIds.policies || tabId === SettingsTabIds.policiesMid)) {
            realRefName += "*";
        }
        const routeValues: { [key: string]: string } = {};

        const project = this.getProject();
        if (project) {
            routeValues["project"] = project.name;
        }

        routeValues["adminPivot"] = "repositories";

        if (tabId) {
            routeValues[SettingsQueryStringParams.tabId] = tabId;
        }

        if (repositoryId) {
            routeValues[SettingsQueryStringParams.repositoryId] = repositoryId;
        }

        if (realRefName) {
            routeValues[SettingsQueryStringParams.refName] = realRefName;
        }

        return routeUrl(this.pageContext, "ms.vss-admin-web.project-admin-hub-route", routeValues);
    }

    public getUrlParameters = (): { [key: string]: string } => {
        const pageHistoryService = this.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        return pageHistoryService.getProjectedState();
    };

    public showErrorMessage = (error: any, format?: string): void => {
        const formattedMessage = getMessage(error.toString(), format);
        if (this.messagesService) {
            this.messagesService.showMessage(
                {
                    renderMessage: () => React.createElement("span", undefined, formattedMessage),
                    severity: MessageCardSeverity.Error
                },
                true
            );
        }
    };

    private getProject = (): IProjectData | undefined => {
        if (!this.projectCache) {
            const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
            const pageServiceData = tfsPageService.getData();
            this.projectCache = pageServiceData && pageServiceData.project;
        }
        return this.projectCache;
    };
}

function getMessage(message: string, format?: string): string {
    if (format) {
        return StringUtil.format(format, message);
    }

    return message;
}

Services.add("SettingsViewService", { serviceFactory: SettingsViewService });
